import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getNotebooks,
  createNotebook as apiCreateNotebook,
  updateNotebook as apiUpdateNotebook,
  deleteNotebook as apiDeleteNotebook,
  getNotes,
  createNote as apiCreateNote,
  deleteNote as apiDeleteNote,
  archiveNote as apiArchiveNote,
  unarchiveNote as apiUnarchiveNote,
} from '../services/api';
import type { CreateNotebookRequest, UpdateNotebookRequest, CreateNoteRequest, Note, Notebook } from '@compendium/shared';

/** Find "Quick Notes" notebook as default for new notes, falling back to first notebook. */
export function getDefaultNotebookId(notebooks: Notebook[]): number | undefined {
  const quickNotes = notebooks.find(nb => nb.name.toLowerCase() === 'quick notes');
  return quickNotes?.id ?? notebooks[0]?.id;
}

export function useNotebooks() {
  const queryClient = useQueryClient();

  const { data: notebooks = [], isLoading } = useQuery({
    queryKey: ['notebooks'],
    queryFn: getNotebooks,
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateNotebookRequest) => apiCreateNotebook(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notebooks'] }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateNotebookRequest }) =>
      apiUpdateNotebook(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notebooks'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiDeleteNotebook(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notebooks'] });
      queryClient.invalidateQueries({ queryKey: ['notes'] });
    },
  });

  return {
    notebooks,
    isLoading,
    createNotebook: createMutation,
    updateNotebook: updateMutation,
    deleteNotebook: deleteMutation,
  };
}

export function useNotesList(notebookId?: number, archived?: boolean) {
  const queryClient = useQueryClient();

  const { data: notes = [], isLoading } = useQuery({
    queryKey: ['notes', notebookId ?? 'all', archived ?? false],
    queryFn: () => getNotes(notebookId, archived),
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateNoteRequest) => apiCreateNote(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notes'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiDeleteNote(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notes'] }),
  });

  const archiveMutation = useMutation({
    mutationFn: (id: number) => apiArchiveNote(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notes'] }),
  });

  const unarchiveMutation = useMutation({
    mutationFn: (id: number) => apiUnarchiveNote(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notes'] }),
  });

  return {
    notes,
    isLoading,
    createNote: createMutation,
    deleteNote: deleteMutation,
    archiveNote: archiveMutation,
    unarchiveNote: unarchiveMutation,
  };
}

export function useAllNotesGrouped() {
  const { data: allNotes = [], isLoading } = useQuery({
    queryKey: ['notes', 'all', false],
    queryFn: () => getNotes(undefined, false),
  });

  const grouped = useMemo(() => {
    const map: Record<number, Note[]> = {};
    for (const note of allNotes) {
      if (!map[note.notebookId]) map[note.notebookId] = [];
      map[note.notebookId]!.push(note);
    }
    return map;
  }, [allNotes]);

  return { grouped, allNotes, isLoading };
}
